/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
import { Grid, makeStyles, Tooltip, Typography } from '@material-ui/core';
import RedoIcon from '@material-ui/icons/Redo';
import WarningIcon from '@material-ui/icons/Warning';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import React from 'react';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import {
  Publication,
  PublicationResponse,
  Step,
} from 'modules/environments/interfaces';
import { formatDuration } from 'utils/helpers';
import clsx from 'clsx';
import i18n from 'i18n';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import Button from 'components/Button';
import { calcTotalTime, formatDateWithMonth } from 'utils/tsHelpers';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { Alert } from '@material-ui/lab';
import styles, { overrideModal } from './styles';
import HeaderDetails from './HeaderDetails';
import StepDetails from './StepDetails';

const useStyles = makeStyles(styles);

interface Props {
  publishHistory: PublicationResponse;
  permissions?: PermissionsData;
  modalVersion?: boolean;
  requestInfos: () => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  setCurrentTab?: React.Dispatch<React.SetStateAction<number>>;
  reopenModal?: () => void;
}

const OptimizedPublication = ({
  publishHistory,
  permissions,
  modalVersion,
  requestInfos,
  modalOpen,
  modalClose,
  setCurrentTab,
  reopenModal,
}: Props) => {
  const classes = useStyles();
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);

  React.useEffect(() => {
    requestInfos();
  }, []);

  const isPublicationRunning =
    publishHistory?.publications?.[0]?.status === 'RUNNING';

  React.useEffect(() => {
    if (isPublicationRunning) {
      const runningStep = publishHistory?.publications[0].steps.find(
        step => step.status === 'RUNNING'
      );

      const correspondingTemplateStep = publishHistory?.publication_template_steps.find(
        templateStep => templateStep?.name === runningStep?.name
      );

      const intervalDuration = Math.min(
        correspondingTemplateStep?.duration_milliseconds || 5000,
        5000
      );

      const intervalId = setInterval(() => {
        requestInfos();
      }, intervalDuration);

      return () => clearInterval(intervalId);
    }

    return undefined;
  }, [publishHistory]);

  const runningTimeTotal = calcTotalTime(
    publishHistory?.publication_template_steps
  );

  const handleHeaderDetails = (item: Publication) => {
    modalOpen({
      title: `${i18n.t('PUBLICATION_DETAILS_KEY')} ${item?.name}`,
      overrideClasses: overrideModal,
      footer: (
        <Grid>
          {modalVersion && (
            <Button
              id="back-button"
              outlineBlue
              variant="outlined"
              onClick={reopenModal}
            >
              {i18n.t('BACK_KEY')}
            </Button>
          )}
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
      content: <HeaderDetails itemFields={item} />,
    });
  };

  const handleCellDetails = (item: Step) => {
    modalOpen({
      title: `${i18n.t('PUBLICATION_STEP_DETAILS_KEY')} ${item?.name}`,
      overrideClasses: overrideModal,
      footer: (
        <Grid>
          {modalVersion && (
            <Button
              id="back-button"
              outlineBlue
              variant="outlined"
              onClick={reopenModal}
            >
              {i18n.t('BACK_KEY')}
            </Button>
          )}
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
      content: <StepDetails itemFields={item} />,
    });
  };

  return (
    <>
      {publishHistory?.publications?.length > 0 ? (
        <Grid container className={classes.container}>
          <Grid
            container
            className={clsx(classes.stepsGrid, {
              [classes.stepsGridModal]: !!modalVersion,
            })}
          >
            <Grid item className={classes.headerLabels}>
              <Grid item className={classes.headerLabelsText}>
                <Typography
                  variant="h6"
                  className={classes.headerLabelsTextTypoInfo}
                >
                  {`${i18n.t('AVERAGE_STEP_TIME_KEY')}:`}
                </Typography>
                <Typography
                  variant="h6"
                  className={classes.headerLabelsTextTypoInfo}
                >
                  {`(${i18n.t('AVERAGE_TOTAL_TIME_KEY')}: ${runningTimeTotal})`}
                </Typography>
              </Grid>
            </Grid>
            <Grid container className={classes.headerInfos}>
              {publishHistory?.publication_template_steps?.map(item => (
                <Grid item className={classes.infoItem} key={item?.name}>
                  <Grid item className={classes.stepName}>
                    <Typography className={classes.stepNameTypo} variant="h6">
                      {item?.name}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.stepDurationHeader}>
                    <Typography
                      className={classes.stepDurationTypoHeader}
                      variant="h6"
                    >
                      {formatDuration(item?.duration_milliseconds)}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          {publishHistory?.publications?.map((item: Publication, index) => (
            <>
              <Grid
                container
                justify="center"
                className={clsx(classes.stepsGrid, {
                  [classes.stepsGridModal]: !!modalVersion,
                })}
                key={item?.name}
                alignItems="center"
              >
                <Grid
                  item
                  style={{
                    borderRadius:
                      index === publishHistory.publications.length - 1
                        ? '0px 0px 0px 8px'
                        : '0px',
                  }}
                  className={clsx(classes.headerLabelsVersion, {
                    [classes.headerLabelsVersionError]:
                      item?.status === 'FAILED' || item?.status === 'TIMEOUT',
                  })}
                  onClick={() => handleHeaderDetails(item)}
                >
                  <Grid item className={classes.infoGridIcon}>
                    <Typography
                      variant="h6"
                      className={clsx({
                        [classes.headerLabelsTextTypoSuccessChip]:
                          item?.status === 'SUCCESS',
                        [classes.headerLabelsTextTypoErrorChip]:
                          item?.status === 'FAILED' ||
                          item?.status === 'TIMEOUT',
                        [classes.headerLabelsTextTypoRunningChip]:
                          item?.status === 'RUNNING',
                        [classes.headerLabelsTextTypoWarningChip]:
                          item?.status === 'WARNING',
                      })}
                    >
                      {item?.name}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.startedAtBox}>
                    <Typography
                      variant="h6"
                      className={classes.headerLabelsTextTypoStarted}
                    >
                      {formatDateWithMonth(item?.started_at)}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.detailExpandGrid}>
                    <Tooltip title={i18n.t('CLICK_HERE_TO_DETAILS_KEY')}>
                      <InfoIcon className={classes.infoIcon} />
                    </Tooltip>
                  </Grid>
                </Grid>

                <Grid container className={classes.headerInfos}>
                  {publishHistory.publication_template_steps.map(
                    templateStep => {
                      const matchingStep = item?.steps?.find(
                        subItem => subItem?.name === templateStep?.name
                      );

                      return (
                        <Grid
                          item
                          className={clsx(classes.infoItem, {
                            [classes.infoItemError]:
                              (item?.status === 'FAILED' ||
                                item?.status === 'TIMEOUT') &&
                              !matchingStep?.duration_milliseconds,
                          })}
                          key={templateStep?.name}
                        >
                          {matchingStep ? (
                            <Grid
                              item
                              onMouseEnter={() =>
                                setHoveredItem(matchingStep?.id)
                              }
                              onClick={() => handleCellDetails(matchingStep)}
                              onMouseLeave={() => setHoveredItem(null)}
                              className={clsx(classes.stepDuration, {
                                [classes.stepDurationSuccess]:
                                  matchingStep?.status === 'SUCCESS',
                                [classes.stepDurationError]:
                                  matchingStep?.status === 'FAILED' ||
                                  matchingStep?.status === 'TIMEOUT',
                                [classes.stepDurationSkipped]:
                                  matchingStep?.status === 'SKIPPED',
                                [classes.stepDurationRunning]:
                                  matchingStep?.status === 'RUNNING',
                                [classes.stepDurationWarning]:
                                  matchingStep?.status === 'WARNING',
                              })}
                            >
                              <>
                                {hoveredItem === matchingStep?.id ? (
                                  <Tooltip
                                    arrow
                                    title={i18n.t('CLICK_HERE_TO_DETAILS_KEY')}
                                  >
                                    <Grid item className={classes.logsCell}>
                                      <Typography
                                        variant="h6"
                                        className={classes.seeDetails}
                                      >
                                        {i18n.t('DETAILS_KEY')}
                                      </Typography>
                                      <EqualizerIcon
                                        className={classes.equalizerIcon}
                                      />
                                    </Grid>
                                  </Tooltip>
                                ) : (
                                  <Grid item>
                                    <Typography
                                      className={clsx(
                                        classes.stepDurationTypo,
                                        {
                                          [classes.stepDurationTypoError]:
                                            matchingStep?.status === 'FAILED' ||
                                            matchingStep?.status === 'TIMEOUT',
                                          [classes.stepDurationTypoRunning]:
                                            matchingStep?.status === 'RUNNING',
                                          [classes.stepDurationTypoOpacity]:
                                            hoveredItem !== matchingStep?.id,
                                          [classes.stepDurationTypoNoOpacity]:
                                            hoveredItem === matchingStep?.id,
                                        }
                                      )}
                                      variant="h6"
                                    >
                                      {matchingStep?.duration_milliseconds
                                        ? formatDuration(
                                            matchingStep?.duration_milliseconds
                                          )
                                        : i18n.t('IN_PROGRESS_KEY')}
                                    </Typography>
                                  </Grid>
                                )}

                                {(matchingStep?.status === 'FAILED' ||
                                  matchingStep?.status === 'TIMEOUT') && (
                                  <Grid item className={classes.errorLabelBox}>
                                    <Tooltip
                                      arrow
                                      title={i18n.t('STAGE_FAILED_KEY')}
                                    >
                                      <Typography
                                        className={classes.failedTextBox}
                                      >
                                        <HighlightOffIcon
                                          className={classes.redoIcon}
                                        />
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                )}
                                {matchingStep?.status === 'SKIPPED' && (
                                  <Grid item className={classes.errorLabelBox}>
                                    <Tooltip
                                      arrow
                                      title={i18n.t('PUBLISH_SKIPPED_KEY')}
                                    >
                                      <Typography
                                        className={classes.skippedTextBox}
                                      >
                                        <RedoIcon
                                          className={classes.redoIcon}
                                        />
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                )}
                                {matchingStep?.status === 'WARNING' && (
                                  <Grid item className={classes.errorLabelBox}>
                                    <Tooltip
                                      arrow
                                      title={i18n.t(
                                        'PUBLICATION_STEP_WARNING_KEY'
                                      )}
                                    >
                                      <Typography
                                        className={classes.warningTextBox}
                                      >
                                        <WarningIcon
                                          className={classes.redoIcon}
                                        />
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                )}
                              </>
                            </Grid>
                          ) : (
                            <Grid item></Grid>
                          )}
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              </Grid>
              {item?.model_version ? (
                <Grid
                  item
                  className={clsx(classes.modelVersionGrid, {
                    [classes.modelVersionGridModal]: !!modalVersion,
                  })}
                  onClick={() => {
                    if (
                      setCurrentTab &&
                      !!permissions &&
                      permissions.list_models
                    ) {
                      setCurrentTab(2);
                    }
                  }}
                >
                  <Typography
                    className={clsx(classes.modelVersionText, {
                      [classes.modelModalVersionText]: !!modalVersion,
                    })}
                  >{`${i18n.t('GENERATED_MODEL_KEY')}: #${
                    item?.model_version
                  }`}</Typography>
                </Grid>
              ) : (
                <Grid
                  item
                  className={clsx(classes.modelVersionGrid, {
                    [classes.modelVersionGridModal]: !!modalVersion,
                  })}
                >
                  <Typography className={classes.modelVersionTextUnavailable}>
                    {i18n.t('UNAVAILABLE_MODEL_KEY')}
                  </Typography>
                </Grid>
              )}
            </>
          ))}
        </Grid>
      ) : (
        <Grid container justify="center">
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography className={classes.alertText}>
                {i18n.t('NOT_ENOUGH_DATA_PUBLISH_LOGS_KEY')}
              </Typography>
            </Grid>
          </Alert>
        </Grid>
      )}
    </>
  );
};

const mapStateToProps = ({
  environments: { publishHistory },
  authenticate: { data },
}: ApplicationState) => ({
  publishHistory,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, { modalOpen, modalClose })(
  OptimizedPublication
);
